import {
  createContext,
  Dispatch,
  isValidElement,
  ReactNode,
  SetStateAction,
  useContext,
  useLayoutEffect,
  useState,
} from "react";
import { equals } from "ramda";

interface BreadcrumbItem {
  label: string;
  href?: string;
}

type HeaderVariant = "default" | "colored";

interface INextGenHeaderContext {
  breadcrumbs: BreadcrumbItem[];
  title: ReactNode;
  actions: [ReactNode?, ReactNode?];
  rightPane: ReactNode;
  variant: HeaderVariant;
  heroContent: ReactNode;
  setBreadcrumbs: Dispatch<SetStateAction<BreadcrumbItem[]>>;
  setTitle: Dispatch<SetStateAction<ReactNode>>;
  setActions: Dispatch<SetStateAction<[ReactNode?, ReactNode?]>>;
  setRightPane: Dispatch<SetStateAction<ReactNode>>;
  setVariant: Dispatch<SetStateAction<HeaderVariant>>;
  setHeroContent: Dispatch<SetStateAction<ReactNode>>;
}

const compareReactNodes = (node1: ReactNode, node2: ReactNode): boolean => {
  if (isValidElement(node1) && isValidElement(node2)) {
    return node1.type === node2.type && equals(node1.props, node2.props);
  }
  return equals(node1, node2);
};

const NextGenHeaderContext = createContext<INextGenHeaderContext>({
  breadcrumbs: [],
  title: null,
  actions: [],
  rightPane: null,
  variant: "default",
  heroContent: null,
  setBreadcrumbs: () => {},
  setTitle: () => {},
  setActions: () => {},
  setRightPane: () => {},
  setVariant: () => {},
  setHeroContent: () => {},
});

export const NextGenHeaderProvider = ({ children }: { children: ReactNode }) => {
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [title, setTitle] = useState<ReactNode>(null);
  const [actions, setActions] = useState<[ReactNode?, ReactNode?]>([]);
  const [rightPane, setRightPane] = useState<ReactNode>(null);
  const [variant, setVariant] = useState<HeaderVariant>("default");
  const [heroContent, setHeroContent] = useState<ReactNode>(null);

  return (
    <NextGenHeaderContext.Provider
      value={{
        breadcrumbs,
        setBreadcrumbs,
        title,
        setTitle,
        actions,
        setActions,
        rightPane,
        setRightPane,
        variant,
        setVariant,
        heroContent,
        setHeroContent,
      }}
    >
      {children}
    </NextGenHeaderContext.Provider>
  );
};

const useNextGenHeader = ({
  breadcrumbs: initialBreadcrumbs,
  title: initialTitle,
  actions: initialActions,
  rightPane: initialRightPane,
  variant: initialVariant,
  heroContent: initialHeroContent,
}: Partial<
  Pick<
    INextGenHeaderContext,
    "breadcrumbs" | "title" | "actions" | "rightPane" | "variant" | "heroContent"
  >
> = {}) => {
  const context = useContext(NextGenHeaderContext);

  if (!context) {
    throw new Error("useNextGenHeader must be used within a NextGenHeaderProvider");
  }

  const { setBreadcrumbs, setTitle, setActions, setRightPane, setVariant, setHeroContent } =
    context;

  useLayoutEffect(() => {
    if (initialBreadcrumbs !== undefined && !equals(initialBreadcrumbs, context.breadcrumbs)) {
      setBreadcrumbs(initialBreadcrumbs);
    }
  }, [initialBreadcrumbs, context.breadcrumbs, setBreadcrumbs]);

  useLayoutEffect(() => {
    if (initialTitle !== undefined && !compareReactNodes(initialTitle, context.title)) {
      setTitle(initialTitle);
    }
  }, [initialTitle, context.title, setTitle]);

  useLayoutEffect(() => {
    if (initialActions !== undefined && !equals(initialActions, context.actions)) {
      setActions(initialActions);
    }
  }, [initialActions, context.actions, setActions]);

  useLayoutEffect(() => {
    if (initialRightPane !== undefined && !compareReactNodes(initialRightPane, context.rightPane)) {
      setRightPane(initialRightPane);
    }
  }, [initialRightPane, context.rightPane, setRightPane]);

  useLayoutEffect(() => {
    if (initialVariant !== undefined && initialVariant !== context.variant) {
      setVariant(initialVariant);
    }
  }, [initialVariant, context.variant, setVariant]);

  useLayoutEffect(() => {
    if (
      initialHeroContent !== undefined &&
      !compareReactNodes(initialHeroContent, context.heroContent)
    ) {
      setHeroContent(initialHeroContent);
    }
  }, [initialHeroContent, context.heroContent, setHeroContent]);

  // Reset all header state when the page unmounts so stale values don't bleed
  // into pages that don't call useNextGenHeader.
  useLayoutEffect(() => {
    return () => {
      setBreadcrumbs([]);
      setTitle(null);
      setActions([]);
      setRightPane(null);
      setVariant("default");
      setHeroContent(null);
    };
  }, [setBreadcrumbs, setTitle, setActions, setRightPane, setVariant, setHeroContent]);

  return context;
};

export type { BreadcrumbItem, HeaderVariant };
export default useNextGenHeader;
